import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ErrorHandlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {ellipsisText, faded} from 'styles/Utilities';
import {Size} from 'ui-library/inputs/InputConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {ElementKeyboardEvent} from 'util/Types';

import {BioentityTagsQuery, BioentityTagsQueryVariables} from './__generated/BioentityTagsQuery';
import {BIOENTITY_TAGS_QUERY} from './IngredientTable';
import TextInputWithSuggestions from './TextInputWithSuggestions';

export interface CatalogIdentifierInputProps {
  value: string | null;
  componentBioentity: {id: string} | null;
  onChange: (e: {target: {value: string}}) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
}

export function CatalogIdentifierInput({
  componentBioentity,
  onChange,
  value,
  onKeyDown,
}: CatalogIdentifierInputProps): JSX.Element {
  function getOptionDatas(tagsJson: JSONScalar): Array<OptionData> {
    return tagsJson
      .filter(
        (tagJson: JSONScalar) =>
          tagJson.value &&
          [
            FieldType.FLOAT,
            FieldType.INTEGER,
            FieldType.TEXT,
            FieldType.LONG_TEXT,
            FieldType.SELECTOR,
          ].includes(tagJson.tagSchemaField?.fieldType)
      )
      .map((tagJson: JSONScalar) => ({
        name: (
          <>
            <div className={css(styles.ellipsisText)}>{tagJson.value}</div>
            <div className={css(styles.ellipsisText, styles.faded)}>{tagJson.name}</div>
          </>
        ),
        value: tagJson.value,
        pureTextName: `${tagJson.name} ${tagJson.value}`, // so autocomplete matches name or value
      }));
  }

  return (
    <ErrorHandlingQuery<BioentityTagsQuery, BioentityTagsQueryVariables>
      query={BIOENTITY_TAGS_QUERY}
      skip={componentBioentity == null}
      variables={componentBioentity ? {bioentityId: componentBioentity.id} : undefined}
      fetchPolicy="cache-and-network"
      renderCachedDataWhenRefetching={true}
    >
      {(result) => {
        const options: Array<OptionData> =
          result.data == null || isInaccessible(result.data.bioentity)
            ? []
            : getOptionDatas(result.data.bioentity.tagsJson);
        return (
          <TextInputWithSuggestions
            optionDatas={options}
            className={css(styles.textInput)}
            size={Size.SMALL}
            value={value ?? ''}
            onChange={onChange}
            onKeyDown={onKeyDown}
            isLoading={result.type === QueryResultType.PENDING}
          />
        );
      }}
    </ErrorHandlingQuery>
  );
}

const styles = StyleSheet.create({
  textInput: {
    display: 'flex',
  },
  ellipsisText,
  faded,
});
